// useViewModalFields.js

import { useMemo } from 'react';
import { isEnumCompatibleFieldType } from 'utilities/FieldTypes';

import { useNodeTypes } from 'context/NodeTypesContext';
import { isBoardGroupableField } from 'utilities/NodelistField';
import {
    DEFAULT_SUBTITLE_FIELD_KEY,
    isSubtitleFieldType,
} from 'utilities/NodeSubtitle';
import {
    filterSecuredSchemaFields,
    isSecuredSchemaField,
    resolveDisplaySchemaFields,
} from 'views/utils/displaySchemaFields';

import {
    filterTableIgnoredFields,
    isTableIgnoredFieldKey,
} from 'views/utils/tableIgnoredFields';

// Collects field metadata for filters, sorts, and board field picking.
const useViewModalFields = ({ typeKey, schemaFields = null }) => {
    const { getRevision, isReady } = useNodeTypes();
    const nodeTypesRevision = getRevision();
    const nodeTypesReady = isReady();

    const resolvedSchemaTarget = useMemo(() => {
        if (!nodeTypesReady || !typeKey) return null;
        return {
            key: typeKey,
            revision: nodeTypesRevision,
        };
    }, [nodeTypesReady, typeKey, nodeTypesRevision]);

    const resolvedSchemaFields = useMemo(() => {
        if (Array.isArray(schemaFields)) return filterSecuredSchemaFields(schemaFields);
        if (!resolvedSchemaTarget?.key) return [];
        return resolveDisplaySchemaFields({ key: resolvedSchemaTarget.key }, { includeHidden: true });
    }, [schemaFields, resolvedSchemaTarget]);

    const propertyFields = useMemo(() => {
        const standardFields = [
            { key: 'autonumber', name: 'autonumber', label: 'ID', type: 'number' },
            { key: 'autokey', name: 'autokey', label: 'Key', type: 'string' },
            { key: 'title', name: 'title', label: 'Title', type: 'string' },
            { key: DEFAULT_SUBTITLE_FIELD_KEY, name: DEFAULT_SUBTITLE_FIELD_KEY, label: 'Subtitle', type: DEFAULT_SUBTITLE_FIELD_KEY },
            { key: 'dateCreated', name: 'dateCreated', label: 'Date Created', type: 'date' },
            { key: 'dateModified', name: 'dateModified', label: 'Date Modified', type: 'date' },
        ];

        const filteredSchema = Array.isArray(resolvedSchemaFields)
            ? resolvedSchemaFields.filter((field) => {
                const fieldKey = field?.name || field?.key;
                return fieldKey
                    && !isTableIgnoredFieldKey(fieldKey)
                    && !isSecuredSchemaField(field);
            })
            : [];

        const seen = new Set();
        return [...standardFields, ...filteredSchema].filter((field) => {
            const fieldKey = field?.name || field?.key;
            if (!fieldKey || seen.has(fieldKey)) return false;
            seen.add(fieldKey);
            return true;
        });
    }, [resolvedSchemaFields]);

    const filterableFields = useMemo(() => {
        const allowed = new Set(['string', 'number', 'boolean', 'date', 'daterange', 'datetime', 'datetimerange', 'money', 'currency', 'percentage', 'enum', 'tags', 'people', 'url', 'relation']);
        const standardFields = [
            { key: 'autonumber', name: 'autonumber', label: 'ID', type: 'number' },
            { key: 'autokey', name: 'autokey', label: 'Key', type: 'string' },
            { key: 'title', name: 'title', label: 'Title', type: 'string' },
            { key: DEFAULT_SUBTITLE_FIELD_KEY, name: DEFAULT_SUBTITLE_FIELD_KEY, label: 'Subtitle', type: DEFAULT_SUBTITLE_FIELD_KEY },
            { key: 'dateCreated', name: 'dateCreated', label: 'Date Created', type: 'date' },
            { key: 'dateModified', name: 'dateModified', label: 'Date Modified', type: 'date' },
        ];

        const filteredSchema = Array.isArray(resolvedSchemaFields)
            ? resolvedSchemaFields.filter((field) => {
                const fieldKey = field?.name || field?.key;
                const fieldType = String(field?.type || '').toLowerCase();
                return fieldKey
                    && allowed.has(fieldType)
                    && !isTableIgnoredFieldKey(fieldKey)
                    && !isSecuredSchemaField(field);
            })
            : [];

        const seen = new Set();
        return [...standardFields, ...filteredSchema].filter((field) => {
            const fieldKey = field?.name || field?.key;
            if (!fieldKey || seen.has(fieldKey)) return false;
            seen.add(fieldKey);
            return true;
        });
    }, [resolvedSchemaFields]);

    const filterFieldMap = useMemo(() => {
        const map = new Map();
        filterableFields.forEach((field) => {
            const fieldKey = field?.name || field?.key;
            if (fieldKey) map.set(fieldKey, field);
        });
        return map;
    }, [filterableFields]);

    const sortFieldOptions = useMemo(() => (
        filterableFields.map((field) => {
            const fieldKey = field?.name || field?.key;
            const label = field?.label || '';
            return { value: fieldKey, label };
        }).filter((opt) => opt?.value)
    ), [filterableFields]);

    const enumFields = useMemo(() => {
        if (!Array.isArray(resolvedSchemaFields)) return [];
        return filterTableIgnoredFields(resolvedSchemaFields)
            .filter((field) => isBoardGroupableField(field));
    }, [resolvedSchemaFields]);

    const timelineTemporalFields = useMemo(() => (
        filterableFields.filter((field) => {
            const fieldType = String(field?.type || '').toLowerCase();
            return fieldType === 'daterange'
                || fieldType === 'datetimerange'
                || fieldType === 'date'
                || fieldType === 'datetime';
        })
    ), [filterableFields]);

    const timelineGroupFields = useMemo(() => (
        filterableFields.filter((field) => {
            const fieldType = String(field?.type || '').toLowerCase();
            return isEnumCompatibleFieldType(fieldType) || fieldType === 'people';
        })
    ), [filterableFields]);

    const subtitleFieldKey = useMemo(() => {
        if (!Array.isArray(resolvedSchemaFields)) return '';
        const match = resolvedSchemaFields.find((field) => isSubtitleFieldType(field?.type));
        return match?.name || match?.key || '';
    }, [resolvedSchemaFields]);

    return {
        propertyFields,
        filterableFields,
        filterFieldMap,
        sortFieldOptions,
        enumFields,
        hasEnumFields: enumFields.length > 0,
        timelineTemporalFields,
        timelineGroupFields,
        subtitleFieldKey,
        hasSubtitleField: Boolean(subtitleFieldKey),
    };
};

export default useViewModalFields;
